import "./PendingCancelLeaveRequests.scss";
import {
  Button,
  DatePicker,
  Empty,
  Grid,
  Input,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
} from "antd";
import { CheckOutlined, CloseOutlined, SearchOutlined } from "@ant-design/icons";
import {
  fetchData,
  fixedTableScrollY,
  Notify,
  RequestStatus,
  rowsPerPage,
} from "../../utils/constants";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LeaveRequestPending } from "../../utils/types";
import { useUser } from "../../context/UserContext";
import RequestStatusModal from "../Modals/RequestStatusModal";
import type { ColumnType, ColumnsType } from "antd/es/table";
import type {
  FilterDropdownProps,
  SortOrder,
} from "antd/es/table/interface";
import { Dayjs } from "dayjs";
import {
  activeColumn,
  DEFAULT_EMPLOYEE_STATUS_FILTER,
  EmployeeStatusFilter,
  EmployeeStatusFilterValue,
  matchesEmployeeStatus,
} from "../Filters/EmployeeStatusFilter";

const SORT_DIRECTIONS: SortOrder[] = ["ascend", "descend"];

const PendingCancelLeaveRequests = () => {
  const { authUser } = useUser();
  const screens = Grid.useBreakpoint();
  const isDesktop = !!screens.md;

  const [leaveRequestsPending, setLeaveRequestsPending] = useState<
    LeaveRequestPending[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] =
    useState<LeaveRequestPending | null>(null);
  const [selectedRequestStatus, setSelectedRequestStatus] =
    useState<RequestStatus>(RequestStatus.APPROVE);
  const [isRequestStatusOpen, setIsRequestStatusOpen] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [requestDateRange, setRequestDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);
  const [employeeStatusFilter, setEmployeeStatusFilter] =
    useState<EmployeeStatusFilterValue>(DEFAULT_EMPLOYEE_STATUS_FILTER);
  const { RangePicker } = DatePicker;

  const fetchLeaveRequests = useCallback(async () => {
    try {
      setIsLoading(true);

      await fetchData(
        `${process.env.REACT_APP_API_URL}/cancellation-requests/pending`,
        setLeaveRequestsPending,
      );
    } catch (error) {
      console.error("Failed to fetch pending leave requests data:", error);
      Notify(
        "error",
        "Could not load pending leave requests data. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authUser) return;
    fetchLeaveRequests();
  }, [authUser, fetchLeaveRequests]);

  useEffect(() => {
    if (!isRequestStatusOpen) {
      fetchLeaveRequests();
    }
  }, [fetchLeaveRequests, isRequestStatusOpen]);

  useEffect(() => {
    setCurrentPage(1);
  }, [requestDateRange, employeeStatusFilter]);

  const filteredLeaveRequestsPending = useMemo(() => {
    const statusFiltered = leaveRequestsPending.filter((request) =>
      matchesEmployeeStatus(request.employeeStatus, employeeStatusFilter),
    );
    if (!requestDateRange?.[0] || !requestDateRange?.[1]) {
      return statusFiltered;
    }

    const filterStart = requestDateRange[0].startOf("day").toDate();
    const filterEnd = requestDateRange[1].endOf("day").toDate();

    return statusFiltered.filter((request) => {
      if (!request.startDate || !request.endDate) return false;
      const requestStart = new Date(request.startDate);
      const requestEnd = new Date(request.endDate);
      return requestStart <= filterEnd && requestEnd >= filterStart;
    });
  }, [leaveRequestsPending, requestDateRange, employeeStatusFilter]);

  const getTextColumnFilterProps = useCallback(
    (
      dataIndex: keyof Pick<LeaveRequestPending, "employeeName" | "comment">,
      placeholder: string,
    ): ColumnType<LeaveRequestPending> => ({
      filterDropdown: ({
        selectedKeys,
        setSelectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }} onKeyDown={(event) => event.stopPropagation()}>
          <Input
            placeholder={placeholder}
            value={(selectedKeys[0] as string) || ""}
            onChange={(event) => {
              const value = event.target.value;
              setSelectedKeys(value ? [value] : []);
            }}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small">
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
              size="small"
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        String(record[dataIndex] || "")
          .toLowerCase()
          .includes(String(value).toLowerCase()),
    }),
    [],
  );

  const columns = useMemo<ColumnsType<LeaveRequestPending>>(
    () => [
      {
        title: "Employee name",
        dataIndex: "employeeName",
        key: "employeeName",
        ...getTextColumnFilterProps("employeeName", "Search employee"),
      },
      activeColumn<LeaveRequestPending>(),
      {
        title: "Start Date",
        dataIndex: "startDate",
        key: "startDate",
        sorter: (a: LeaveRequestPending, b: LeaveRequestPending) =>
          (a.startDate || "").localeCompare(b.startDate || ""),
        sortDirections: SORT_DIRECTIONS,
      },
      {
        title: "End Date",
        dataIndex: "endDate",
        key: "endDate",
        sorter: (a: LeaveRequestPending, b: LeaveRequestPending) =>
          (a.endDate || "").localeCompare(b.endDate || ""),
        sortDirections: SORT_DIRECTIONS,
      },
      {
        title: "Duration (days)",
        dataIndex: "duration",
        key: "duration",
        sorter: (a: LeaveRequestPending, b: LeaveRequestPending) =>
          (a.duration || 0) - (b.duration || 0),
        sortDirections: SORT_DIRECTIONS,
      },
      {
        title: "Date Created",
        dataIndex: "creationDate",
        key: "creationDate",
        render: (value: string) =>
          value
            ? new Date(value).toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "-",
        sorter: (a: LeaveRequestPending, b: LeaveRequestPending) =>
          (a.creationDate || "").localeCompare(b.creationDate || ""),
        defaultSortOrder: "descend" as const,
        sortDirections: SORT_DIRECTIONS,
      },
      {
        title: "Comment",
        dataIndex: "comment",
        key: "comment",
        ...getTextColumnFilterProps("comment", "Search comment"),
      },
      {
        title: "Actions",
        key: "actions",
        fixed: isDesktop ? ("right" as const) : undefined,
        width: 120,
        align: "center" as const,
        render: (_: unknown, record: LeaveRequestPending) => (
          <div className="action-buttons">
            <Tooltip title="Approve cancellation">
              <Button
                type="text"
                className="status-action-btn"
                icon={<CheckOutlined />}
                onClick={() => {
                  setSelectedRequest(record);
                  setSelectedRequestStatus(RequestStatus.APPROVE);
                  setIsRequestStatusOpen(true);
                }}
              />
            </Tooltip>
            <Tooltip title="Reject cancellation">
              <Button
                type="text"
                className="status-action-btn"
                danger
                icon={<CloseOutlined />}
                onClick={() => {
                  setSelectedRequest(record);
                  setSelectedRequestStatus(RequestStatus.REJECT);
                  setIsRequestStatusOpen(true);
                }}
              />
            </Tooltip>
          </div>
        ),
      },
    ],
    [getTextColumnFilterProps, isDesktop],
  );

  return (
    <div className="pending-cancel-requests">
      <Typography.Title level={4} className="table-header">
        Pending Cancelled Leave Requests
      </Typography.Title>
      <div className="mb-2 flex justify-start gap-2 flex-wrap">
        <EmployeeStatusFilter
          value={employeeStatusFilter}
          onChange={setEmployeeStatusFilter}
        />
        <RangePicker
          allowClear
          allowEmpty={[true, true]}
          format="DD/MM/YYYY"
          value={requestDateRange}
          onChange={(range) => setRequestDateRange(range)}
          placeholder={["Start date", "End date"]}
        />
      </div>
      {isRequestStatusOpen && (
        <RequestStatusModal
          isOpen={isRequestStatusOpen}
          setIsOpen={setIsRequestStatusOpen}
          request={selectedRequest ? selectedRequest : null}
          status={selectedRequestStatus}
          source={"default"}
        />
      )}

      <div className="table-container">
        <Table
          className="view-table"
          columns={columns}
          dataSource={filteredLeaveRequestsPending}
          rowKey={(record) =>
            `${record.originalRequestId}-${record.employeeName}`
          }
          scroll={{ x: "max-content", y: fixedTableScrollY }}
          locale={{
            emptyText: isLoading ? (
              <div className="table-loading">
                <Spin size="small" />
                <p>Loading pending cancel requests...</p>
              </div>
            ) : leaveRequestsPending.length === 0 ? (
              <Empty description="No cancel leave requests found." />
            ) : filteredLeaveRequestsPending.length === 0 ? (
              <Empty description="No requests found for the selected filters." />
            ) : null,
          }}
          pagination={
            filteredLeaveRequestsPending.length > rowsPerPage
              ? {
                  current: currentPage,
                  pageSize: rowsPerPage,
                  showSizeChanger: false,
                  position: ["bottomCenter"],
                }
              : false
          }
          onChange={(pagination) => {
            setCurrentPage(pagination.current || 1);
          }}
        />
      </div>
    </div>
  );
};

export default PendingCancelLeaveRequests;
